import { useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { ListItem, ListItemButton, ListItemText, ListItemIcon, Collapse, Tooltip, Box } from '@mui/material';

import RotableChevron from 'modules/common/components/Icons/RotableChevron';
import { MenuItem } from 'modules/common/components/Layout/Sidebar/Sidebar.interface';
import MenuItemBadge from 'modules/common/components/MenuItemBadge';
import SidebarList from 'modules/common/components/Layout/Sidebar/common/SidebarList';
import SubMenu from 'modules/common/components/Layout/Sidebar/SubMenu';
import useThemeBreakpoint from 'modules/common/useThemeBreakpoint';
import { useLayoutContext } from 'modules/common/components/Layout/LayoutContext';
import LockIconSvg from 'assets/collectiveIcons/LockIcon.svg';
import theme from 'theme';

const getSecondLevelPath = (pathname: string) => {
    return pathname.split('/').slice(0, 3).join('/');
};

const getIsChildItemSelected = (
    pathname: string,
    basePath: string,
    submenu?: MenuItem[],
    isSidebarCollapsed?: boolean,
    isSubMenuSelected?: boolean
) => {
    return (
        pathname.startsWith(basePath) &&
        submenu &&
        submenu.findIndex((item) => pathname === item.path) !== -1 &&
        (isSidebarCollapsed || !isSubMenuSelected)
    );
};

function LockIndicator() {
    return (
        <Box
            component="img"
            src={LockIconSvg}
            alt="Lock"
            sx={{
                width: theme.spacing(2), // 16px = 2 units
                height: theme.spacing(2), // 16px = 2 units
                marginRight: 1,
                opacity: 0.7,
            }}
        />
    );
}

export default function Item({
    isSidebarCollapsed,
    subMenuSelected,
    setSubMenuSelected,
    isSubMenu = false,
    ...item
}: MenuItem & {
    isSidebarCollapsed?: boolean;
    subMenuSelected: string | null;
    setSubMenuSelected: (item: string | null) => void;
    isSubMenu?: boolean;
}) {
    const { pathname } = useLocation();
    const history = useHistory();
    const breakpoint = useThemeBreakpoint();
    const { setIsSidebarHidden, navigationInterceptor } = useLayoutContext();

    const itemRef = useRef<HTMLLIElement>(null);

    const secondLevelPath = getSecondLevelPath(pathname);
    const isSubMenuSelected = subMenuSelected === item.basePath;
    const isChildItemSelected = getIsChildItemSelected(
        secondLevelPath,
        item.basePath || '',
        item.submenu,
        isSidebarCollapsed,
        isSubMenuSelected
    );

    const isSelected = (Boolean(item.path) && secondLevelPath === item.path) || isChildItemSelected;

    const isLocked = item.locked;

    const onClick = (selectedItem?: MenuItem) => {
        // Don't allow navigation if menu item is locked
        if (isLocked) {
            return;
        }

        /**
         * Logic here is:
         * If the user opens or closes the sidebar item
         * but its still in the same path of the parent collapsable
         * it won't be redirected.
         * If the user opens a different collapsable
         * it will be redirected to the first submenu item.
         * If the user clicks on an item with a direct path
         * it will be redirected to that path.
         */
        let path = selectedItem?.path;

        if (!path && !isSidebarCollapsed && breakpoint === 'large') {
            const firstSubmenu = item.basePath && item.submenu?.[0];
            if (firstSubmenu && item.submenu?.findIndex((submenu) => pathname.startsWith(submenu.path || '')) === -1) {
                path = firstSubmenu.path;
            }
        }

        if (path) {
            // Check if there's a navigation interceptor that blocks this navigation
            if (navigationInterceptor && !navigationInterceptor(path)) {
                return;
            }

            if (breakpoint !== 'large') {
                setIsSidebarHidden(true);
            }

            history.push(path);
            item.onClickCallback?.();
        }
        if (item?.submenu) {
            setSubMenuSelected(!isSubMenuSelected ? item.basePath : null);
        }
    };

    const isMenuExpanded = isSubMenuSelected && !isSidebarCollapsed;

    const Icon = item.icon;

    useEffect(() => {
        if (isSidebarCollapsed) {
            setSubMenuSelected(null);
        }
    }, [isSidebarCollapsed]);

    const color = isSelected ? 'black' : 'cement.700';

    return (
        <ListItem
            disablePadding
            sx={{
                display: 'block',
            }}
            dense={isSubMenu}
            ref={itemRef}>
            <Tooltip
                title={isLocked && typeof item.lockedTooltip === 'string' ? item.lockedTooltip : ''}
                placement="right"
                // describeChild keeps the row's own accessible name; without it MUI sets aria-label
                // on the child and the tooltip copy replaces the menu item label for screen readers.
                describeChild>
                <ListItemButton
                    onClick={() => onClick(item)}
                    selected={isSelected}
                    // aria-disabled rather than MUI's `disabled`, which sets pointer-events:none and would
                    // swallow the hover the locked tooltip needs. onClick already returns early when locked.
                    aria-disabled={isLocked || undefined}
                    tabIndex={isLocked ? -1 : undefined}
                    disableRipple
                    sx={{
                        '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus': {
                            backgroundColor: 'cement.200',
                        },
                        '&:hover': {
                            backgroundColor: isLocked ? 'transparent' : 'cement.100',
                        },
                        // Keeps locked rows looking exactly as they did under MUI's disabled styling
                        '&[aria-disabled="true"]': {
                            opacity: 1,
                            '& .MuiListItemText-primary': {
                                color: 'cement.700',
                            },
                        },
                        ...(isSubMenu && { paddingTop: 1, paddingBottom: 1 }),
                        ...(isSidebarCollapsed && isSubMenuSelected && { backgroundColor: 'cement.100' }),
                    }}>
                    {Icon && (
                        <Tooltip
                            title={item.name}
                            placement="right"
                            slotProps={{
                                popper: {
                                    onClick: (e: React.MouseEvent) => e.stopPropagation(),
                                    sx: {
                                        pointerEvents: 'none',
                                    },
                                },
                            }}
                            disableTouchListener={!isSidebarCollapsed}
                            disableFocusListener={!isSidebarCollapsed}
                            disableHoverListener={!isSidebarCollapsed}>
                            <ListItemIcon
                                sx={{
                                    display: item.icon ? 'flex' : 'none',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: 24,
                                    height: 24,
                                    minWidth: 24,
                                    padding: 0,
                                    marginRight: '10px',
                                }}>
                                <Icon fontSize="small" sx={{ color, display: 'block', scale: 1.125 }} />
                            </ListItemIcon>
                        </Tooltip>
                    )}
                    <ListItemText
                        primary={item.name}
                        sx={{
                            transition: 'opacity 0.2s ease-in-out',
                            ...((!Icon || isSubMenu) && { pl: '2px' }),
                            ...(!isSidebarCollapsed ? { opacity: 1, my: 0 } : { opacity: 0 }),
                            // Keep text color consistent regardless of lock state
                            color: isLocked ? 'text.primary' : 'inherit',
                        }}
                    />
                    {!isSidebarCollapsed && item.badge && (
                        <MenuItemBadge label={item.badge.label} variant={item.badge.variant} />
                    )}
                    {!isSidebarCollapsed &&
                        item.submenu &&
                        (isLocked ? (
                            <LockIndicator />
                        ) : (
                            <RotableChevron
                                sx={{ marginRight: 1 }}
                                fontSize="small"
                                from="right"
                                to="down"
                                isRotated={isMenuExpanded}
                            />
                        ))}
                    {!isSidebarCollapsed && !item.submenu && isLocked && <LockIndicator />}
                </ListItemButton>
            </Tooltip>
            {item.submenu && !isSidebarCollapsed && (
                <Collapse in={isMenuExpanded} appear={false} sx={{ paddingLeft: 4 }}>
                    <SidebarList sx={{ padding: 0 }}>
                        {item.submenu.map((submenu) => (
                            <Item
                                isSubMenu
                                subMenuSelected={subMenuSelected}
                                setSubMenuSelected={setSubMenuSelected}
                                isSidebarCollapsed={isSidebarCollapsed}
                                {...submenu}
                                key={submenu.name}
                            />
                        ))}
                    </SidebarList>
                </Collapse>
            )}
            {item.submenu && isSidebarCollapsed && (
                <SubMenu isOpen={isSubMenuSelected} anchorEl={itemRef.current} onSelect={onClick} {...item} />
            )}
        </ListItem>
    );
}
